/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useRouter} from 'next/compat/router';
import useLoadCVSAssetIds from '@ion/query/src/hooks/useLoadCVSAssetIds';
import {Route} from '@/routes/RouteDefinitions';
import {filterModelConverterV2} from '@/routes/tracking/trackingUtils';
import {setGridState} from '@/store/actions/tracking';
import {isCurrentPage} from '@/store/helpers';
import {gridFilterModelSelector} from '@/store/selectors/tracking-selectors';
import {attributeMappings} from '../../views/grid/types';
import useGridRequestParams from '../../views/grid/useGridRequestParams';

/**
 * useFilteredAssetIds hook to retrieve a filtered list of asset IDs based on
 * the current filter model. This hook integrates with Redux for state management
 * and uses custom hooks to fetch data and manage request parameters.The hook
 * ensures that the request parameters are updated whenever the filter model changes,
 * and it fetches the filtered asset IDs accordingly. It also dispatches an action
 * to update the filter data in the Redux store.
 * 
 * - **Filter Application**: Generates request parameters based on the current filter model.
 * - **Data Fetching**: Loads filtered asset IDs when filters are applied.
 * - **Redux Integration**: Updates the Redux state with filter application status.
 * 
 * ```typescript
 *  import useFilteredAssetIds from './useFilteredAssetIds';
 * 
 *  const filteredAssetIds = useFilteredAssetIds();
 * 
 * ```
 * 
 * @returns Array of asset IDs just account of filter applied in grid.
 */
export const useFilteredAssetIds = () => {
  const dispatch                          = useDispatch();
  const route                             = useRouter();
  const filterModel                       = useSelector(gridFilterModelSelector);
  const {apiParams, getRequestParams}     = useGridRequestParams();
  const [requestparams, setRequestParams] = useState({params: {}, filterApplied: false});

  const isTrackingBeta = useMemo(() =>
    isCurrentPage(route?.route, Route.TRACKING2)
  , [route?.route]);

  useEffect(() => {
    const {params, filterApplied} = getRequestParams({
      filter : filterModelConverterV2(filterModel as any, attributeMappings),
      sort   : 'assetName'
    });
    setRequestParams({params, filterApplied});
  }, [filterModel, apiParams]);

  const {filterApplied, params} = requestparams;

  const {results: filteredIds} = useLoadCVSAssetIds({
    enabled : filterApplied,
    isTrackingBeta,
    params
  });

  useEffect(() => {
    dispatch(setGridState({filterApplied}));
  }, [filteredIds]);

  return filteredIds;
};

export default useFilteredAssetIds;
